import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { App, Button, DatePicker, Form, Input, Modal, notification, Radio, Select, Space, Table, Tag } from 'antd'
import dayjs from '@plugins/local-dayjs'

import SelectExam from '../../components/select-exam/SelectExam'
import ExamApplyUnitList from '../../request/ExamApplyUnitList'
import ExamPositionList from '../../request/ExamPositionList'
import { checkPage, getTagList } from '../../common/request'
import ResponseCode from '../../common/response-code'
import ExamineeInfoModal from '../../components/examinee/ExamineeInfoModal'
import {
  examCheckStatus,
  isExamRecheckEnabled,
  reviewStageMap
} from '../../common/common'
import { throttle } from '../../plugins/utils'
import DeIdentification from '../../components/DeIdentification'
import { updateOrgTagList } from '../../store/slice/org-slice'

const formatDateTimeCell = (value) => {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm').toString() : '--'
}

function ExamCheckManagement () {
  const [form] = Form.useForm()
  const unitCode = Form.useWatch('unitCode', form)
  const { modal } = App.useApp()
  const dispatch = useDispatch()
  const examineeInfoModalRef = useRef()
  const [currentEditItem, setCurrentEditItem] = useState({})
  const selectedExam = useSelector(state => state.examInfo.selectedExam)
  const orgTagList = useSelector(state => state.org.orgTagList)
  const userInfo = useSelector(state => state.userInfo.userInfo)
  const [checkStatus, setCheckStatus] = useState('PENDING_REVIEW_STAGE')
  const [unitListOption, setUnitListOption] = useState([])
  const [positionListOption, setPositionListOption] = useState([])
  const [tableData, setTableData] = useState({})
  const [pageSize, setPageSize] = useState(10)
  const [pageNum, setPageNum] = useState(1)
  const [searchVersion, setSearchVersion] = useState(0)
  const [loading, setLoading] = useState(false)
  const [tagListOption, setTagListOption] = useState([])
  const [checkStatusOptions, setCheckStatusOptions] = useState([])

  const RECHECK_FIRST_REVIEW_STAGE = {
    label: '待初审',
    value: 'PENDING_REVIEW_STAGE',
    checkStatus: 1,
    reviewStage: 1
  }

  const RECHECK_SECOND_REVIEW_STAGE = {
    label: '待复审',
    value: 'RECHECK_SECOND_REVIEW_STAGE',
    checkStatus: 1,
    reviewStage: 2
  }

  const PENDING_REVIEW_STAGE = {
    label: '待审核',
    value: 'PENDING_REVIEW_STAGE',
    checkStatus: 1
  }

  const RECHECK_FIRST_PASSED_REVIEW_STAGE = {
    label: '初审通过',
    value: 'RECHECK_FIRST_PASSED_REVIEW_STAGE',
    checkStatus: 1,
    reviewStage: 2
  }

  const PASSED_REVIEW_STAGE = {
    label: '审核通过',
    value: 'PASSED_REVIEW_STAGE',
    checkStatus: 2,
    reviewStage: ''
  }

  const UNPASSED_REVIEW_STAGE = {
    label: '审核不通过',
    value: 'UNPASSED_REVIEW_STAGE',
    checkStatus: -1,
    reviewStage: ''
  }

  const checkStatusMap = {
    RECHECK_FIRST_REVIEW_STAGE,
    RECHECK_FIRST_PASSED_REVIEW_STAGE,
    RECHECK_SECOND_REVIEW_STAGE,
    PENDING_REVIEW_STAGE,
    PASSED_REVIEW_STAGE,
    UNPASSED_REVIEW_STAGE,
  }

  useEffect(() => {
    let options = []
    if (selectedExam.recheck === 1 && ['superAdmin', 'operator', 'organization', 'applySiteCharger', 'checker', 'applyRechecker'].includes(userInfo.currentRoleTag)) {
      if (['superAdmin', 'operator', 'organization', 'applySiteCharger'].includes(userInfo.currentRoleTag)) {
        options = [
          RECHECK_FIRST_REVIEW_STAGE,
          RECHECK_SECOND_REVIEW_STAGE,
          PASSED_REVIEW_STAGE,
          UNPASSED_REVIEW_STAGE
        ]
      }

      if (userInfo.currentRoleTag === 'checker') {
        options = [
          RECHECK_FIRST_REVIEW_STAGE,
          RECHECK_FIRST_PASSED_REVIEW_STAGE,
          UNPASSED_REVIEW_STAGE
        ]
      }

      if (userInfo.currentRoleTag === 'applyRechecker') {
        options = [
          RECHECK_SECOND_REVIEW_STAGE,
          PASSED_REVIEW_STAGE,
          UNPASSED_REVIEW_STAGE
        ]
      }
    } else {
      options = [
        PENDING_REVIEW_STAGE,
        PASSED_REVIEW_STAGE,
        UNPASSED_REVIEW_STAGE,
      ]
    }

    setCheckStatusOptions(options)

    const defaultOption = options[0]
    if (defaultOption) {
      setTableData({ elements: [], totalElements: 0 })
      setCheckStatus(defaultOption.value)
      form.setFieldValue('reviewStage', defaultOption.reviewStage ?? '')
      setPageNum(1)
    }
  }, [selectedExam, userInfo.currentRoleTag, form])

  useEffect(() => {
    setTagListOption([
      {
        name: '无标签',
        label: '无标签',
        value: 'no-has',
        id: 'no-has'
      },
      ...orgTagList.map(item => {
        return {
          id: item.id,
          name: item.name,
          color: item.color,
          label: item.name,
          value: item.id
        }
      })
    ])
  }, [orgTagList])

  const columns = useMemo(() => {
    const checkInfoColumns = isExamRecheckEnabled(selectedExam)
      ? [
        {
          width: 100,
          title: '初审人',
          key: 'initialCheckPersonName',
          dataIndex: 'initialCheckPersonName',
          render: (value) => value || '--'
        },
        {
          width: 140,
          title: '初审时间',
          key: 'initialCheckTime',
          dataIndex: 'initialCheckTime',
          render: formatDateTimeCell
        },
        {
          width: 100,
          title: '复审人',
          key: 'reviewer',
          dataIndex: 'reviewer',
          render: (value) => value || '--'
        },
        {
          width: 140,
          title: '复审时间',
          key: 'reviewDate',
          dataIndex: 'reviewDate',
          render: formatDateTimeCell
        }
      ]
      : [
        {
          title: '审核时间',
          key: 'lastCheckTime',
          dataIndex: 'lastCheckTime',
          render: formatDateTimeCell
        },
        {
          title: '审核人',
          key: 'checkPerson',
          dataIndex: 'checkPerson'
        }
      ]

    return [
      {
        width: 90,
        title: '姓名',
        key: 'name',
        dataIndex: 'name',
        fixed: 'left',
        render: (record) => {
          return (<DeIdentification value={ record } type={ 'name' }></DeIdentification>)
        }
      },
      {
        width: 160,
        title: '报名序号',
        key: 'examineesNumber',
        dataIndex: 'examineesNumber'
      },
      {
        width: 190,
        title: '身份证号',
        key: 'idCard',
        dataIndex: 'idCard',
        render: (record) => {
          return (<DeIdentification value={ record } type={ 'name' }></DeIdentification>)
        }
      },
      {
        width: 90,
        title: '单位代码',
        key: 'unitCode',
        dataIndex: 'unitCode'
      },
      {
        title: '单位名称',
        key: 'unitName',
        dataIndex: 'unitName'
      },
      {
        width: 90,
        title: '职位代码',
        key: 'positionCode',
        dataIndex: 'positionCode'
      },
      {
        title: '职位名称',
        key: 'positionName',
        dataIndex: 'positionName'
      },
      {
        title: '提交时间',
        key: 'submitTime',
        dataIndex: 'submitTime',
        render: formatDateTimeCell
      },
      ...checkInfoColumns,
      {
        width: 140,
        title: '标签',
        dataIndex: 'checkTag',
        render: (checkTag) => {
          if (checkTag) {
            return (
              <div className="d-flex align-items-center">
                <div
                  className="px-1" style={ {
                  background: `${ checkTag.color }47`,
                  border: `1px solid ${ checkTag.color }`,
                  borderRadius: 4
                } }>
                  { checkTag.name }
                </div>
              </div>
            )
          }
        }
      },
      {
        width: 100,
        title: '审核阶段',
        key: 'reviewStage',
        dataIndex: 'reviewStage',
        fixed: 'right',
        render: (record) => {
          const stage = reviewStageMap[record]
          return stage ? <Tag color={ stage.color }>{ stage.label }</Tag> : '-'
        }
      },
      {
        width: 100,
        title: '审核状态',
        key: 'saveStatus',
        dataIndex: 'saveStatus',
        fixed: 'right',
        render: (record) => {
          return (<div className={ examCheckStatus[record].className }>{ examCheckStatus[record].label }</div>)
        }
      },
      {
        width: 160,
        title: '操作',
        key: 'operation',
        dataIndex: 'operation',
        fixed: 'right',
        render: (record, row) => {
          return (
            <Space>
              <Button
                size={ 'small' }
                type={ 'primary' }
                ghost
                onClick={ showExamineeDetail.bind(this, row) }
              >详情</Button>
            </Space>
          )
        }
      }
    ].filter(col => {
      if (col.key === 'reviewStage' && !isExamRecheckEnabled(selectedExam)) {
        return false
      }
      return true
    })
  }, [selectedExam])

  const tableScrollX = isExamRecheckEnabled(selectedExam) ? 2100 : 1660

  useEffect(() => {
    if (selectedExam.id && !isExamRecheckEnabled(selectedExam)) {
      form.setFieldValue('reviewStage', '')
    }
  }, [selectedExam.id, selectedExam.recheck])

  useEffect(() => {
    if (selectedExam.id && unitListOption.length > 0) {
      getTagListAction()
      getTableData()
    }
  }, [selectedExam, checkStatus, unitListOption, pageSize, pageNum, searchVersion])

  const onSearchParamsChange = () => {
    setPageNum(1)
    setSearchVersion(version => version + 1)
  }

  const filterTagOption = (input, option) => {
    const keyword = input.trim().toLowerCase()
    if (!keyword) {
      return true
    }
    const label = (option?.label ?? option?.name ?? '').toString().toLowerCase()
    return label.includes(keyword)
  }

  const getTableData = () => {
    const searchData = form.getFieldsValue()
    setLoading(true)
    const params = {
      ...searchData,
      unitCodes: searchData.unitCode
        ? [searchData.unitCode]
        : unitListOption.filter(unitItem => unitItem.value).map(unitItem => unitItem.value),
      submitStartTime: searchData.submitTimeRange?.length > 0
        ? dayjs(searchData.submitTimeRange[0]).format('YYYY-MM-DD HH:mm:ss')
        : [],
      submitEndTime: searchData.submitTimeRange?.length > 0
        ? dayjs(searchData.submitTimeRange[1]).format('YYYY-MM-DD HH:mm:ss')
        : [],
      checkStartTime: searchData.checkTimeRange?.length > 0
        ? dayjs(searchData.checkTimeRange[0]).format('YYYY-MM-DD HH:mm:ss')
        : [],
      checkEndTime: searchData.checkTimeRange?.length > 0
        ? dayjs(searchData.checkTimeRange[1]).format('YYYY-MM-DD HH:mm:ss')
        : [],
      examId: selectedExam.id,
      orderBy: 'examineesNumber',
      orderType: 'ASC',
      pageNum,
      pageSize,
      saveStatusSet: [checkStatusMap[checkStatus].checkStatus],
      checkTagIds: searchData.checkTagIds?.length > 0
        ? searchData.checkTagIds.map(item => {
          if (item === 'no-has') {
            return null
          } else {
            return item
          }
        })
        : undefined
    }
    delete params.unitCode

    if (!isExamRecheckEnabled(selectedExam)) {
      delete params.reviewStage
    }

    checkPage(params).then(res => {
      setLoading(false)
      if (res.code === ResponseCode.success) {
        setTableData(res.data)
      } else {
        Modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      console.error(err)
      Modal.error({
        title: '错误',
        content: '网络错误，查询审核考生列表失败'
      })
    })
  }

  const showExamineeDetail = (row) => {
    setCurrentEditItem(row)

    examineeInfoModalRef.current.showModal()
  }

  const getApplyUnitList = (unitList) => {
    const initOptions = unitList.map(item => {
      return {
        label: `${ item.unitName }_${ item.unitCode }`,
        value: item.unitCode
      }
    })

    initOptions.unshift({
      label: '全部',
      value: ''
    })

    setUnitListOption(initOptions)
  }

  const getApplyPositionList = (positionList) => {
    setPositionListOption([{
      label: '全部',
      value: ''
    }, ...positionList.map(item => {
      return {
        label: `${ item.positionName }_${ item.positionCode }`,
        value: item.positionCode
      }
    })])
  }

  const checkStatusChange = (e) => {
    setTableData({ elements: [], totalElements: 0 })
    const nextStatus = e.target.value
    setCheckStatus(nextStatus)
    const selectedOption = checkStatusOptions.find(item => item.value === nextStatus)
    form.setFieldValue('reviewStage', selectedOption?.reviewStage ?? '')
    setPageNum(1)
  }

  const paginationChange = (num, size) => {
    setPageSize(size)
    if (size === pageSize) {
      setPageNum(num)
    } else {
      setPageNum(1)
    }
  }

  const unitCodeChange = () => {
    form.setFieldValue('positionCode', '')
  }

  const deleteExamineeTag = (row) => {
    setLoading(true)
    deleteExamineeTag({ applyId: row.applyId }).then(res => {
      if (res.code === ResponseCode.success) {
        notification.success({
          message: '操作成功'
        })
        getTableData()
      } else {
        modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      console.error(err)
      modal.error({
        title: '错误',
        content: '网络错误，删除标签失败'
      })
    }).finally(() => {
      setLoading(false)
    })
  }

  const getTagListAction = () => {
    getTagList({
      orgId: selectedExam.orgId,
      status: 1
    }).then(res => {
      if (res.code === ResponseCode.success) {
        dispatch(updateOrgTagList({
          tagList: res.data
        }))
      } else {
        modal.error({
          title: '错误',
          content: res.msg
        })
      }
    }).catch(err => {
      console.error(err)
      modal.error({
        title: '错误',
        content: '网络错误，查询标签列表失败'
      })
    })
  }

  // const getExamineeCheckBtnVisible = (statue) => {
  //   const currentTime = new Date().getTime()
  //   return statue === 1 || (statue === -1 && (selectedExam.checkStartTime < currentTime && currentTime < selectedExam.checkEndTime))
  // }



  return (<>
    <SelectExam>
      <ExamApplyUnitList examId={ selectedExam.id } loadSuccess={ getApplyUnitList }></ExamApplyUnitList>
      <ExamPositionList examId={ selectedExam.id } unitCode={ unitCode } loadSuccess={ getApplyPositionList }></ExamPositionList>

      <Space direction="vertical">
        <Radio.Group value={ checkStatus } options={ checkStatusOptions } optionType="button" buttonStyle="solid" onChange={ checkStatusChange }></Radio.Group>
        <Form
          layout={ 'inline' }
          form={ form }
          initialValues={ {
            unitCode: '',
            positionCode: '',
            name: '',
            idCard: '',
            examineesNumber: '',
            submitTimeRange: [],
            checkTimeRange: [],
            reviewStage: 1
          } }
          onFieldsChange={ throttle(onSearchParamsChange, 500) }
        >
          <Space wrap size={ 16 }>
            <Form.Item hidden name="reviewStage">
              <Input></Input>
            </Form.Item>
            <Form.Item
              label={ '报考单位' }
              name={ 'unitCode' }>
              <Select
                options={ unitListOption }
                style={ { width: 360 } }
                onChange={ unitCodeChange }></Select>
            </Form.Item>
            <Form.Item
              label={ '报考职位' }
              name={ 'positionCode' } dependencies={ ['unitCode'] }>
              <Select
                disabled={ !form.getFieldValue('unitCode') }
                options={ positionListOption }
                style={ { width: 300 } }></Select>
            </Form.Item>
            <Form.Item
              label={ '姓名' }
              name={ 'name' }
              trigger={ 'onInput' }>
              <Input placeholder={ '请输入姓名' }></Input>
            </Form.Item>
            <Form.Item
              label={ '身份证' }
              name={ 'idCard' }
              trigger={ 'onInput' }>
              <Input placeholder={ '请输入身份证' }></Input>
            </Form.Item>
            <Form.Item
              label={ '报名序号' }
              name={ 'examineesNumber' }
              trigger={ 'onInput' }>
              <Input placeholder={ '请输入报名序号' }></Input>
            </Form.Item>
            <Form.Item
              label={ '提交审核时间' }
              name={ 'submitTimeRange' }>
              <DatePicker.RangePicker
                format="YYYY-MM-DD HH:mm"
                showTime={ { defaultValue: [dayjs('09:00:00', 'HH:mm'), dayjs('18:00:00', 'HH:mm')] } }
                style={ { width: '100%' } }></DatePicker.RangePicker>
            </Form.Item>
            <Form.Item
              label={ '审核时间' }
              name={ 'checkTimeRange' }>
              <DatePicker.RangePicker
                format="YYYY-MM-DD HH:mm"
                showTime={ { defaultValue: [dayjs('09:00:00', 'HH:mm'), dayjs('18:00:00', 'HH:mm')] } }
                style={ { width: '100%' } }></DatePicker.RangePicker>
            </Form.Item>
            <Form.Item
              label={ '标签' }
              name={ 'checkTagIds' }>
              <Select
                mode="tags"
                showSearch
                optionFilterProp="label"
                filterOption={ filterTagOption }
                options={ tagListOption }
                style={ { width: 300 } }
                placeholder="选择或输入标签筛选考生"
                optionRender={ (option, info) => {
                  return (<div className="d-flex align-center">
                    <div
                      className="mr-2" style={ {
                      width: 20,
                      height: 20,
                      background: option.data.color,
                      borderRadius: 4
                    } }></div>
                    <div>{ option.data.name }</div>
                  </div>)
                } }
                tagRender={ (tagProps) => {
                  const currentTag = tagListOption.find(item => item.id === tagProps.value)

                  return (
                    <div
                      className="mr-1 d-flex align-center"
                      style={ {
                        background: `${ currentTag?.color || '#dddddd' }47`,
                        color: '#222',
                        border: `1px solid ${ currentTag?.color || '#222222' }`,
                        fontSize: 12,
                        padding: '0 4px',
                        borderRadius: 2
                      } }
                    >
                      { currentTag?.name }
                    </div>
                  )
                } }
              ></Select>
            </Form.Item>
          </Space>
        </Form>
      </Space>
      <Table
        className={ 'mt-3' }
        loading={ loading }
        scroll={ {
          x: tableScrollX
        } }
        columns={ columns }
        dataSource={ tableData.elements }
        rowKey={ record => record.id }
        pagination={ {
          showTotal: (total) => {
            return `共 ${ total } 条`
          },
          size: 'normal',
          showSizeChanger: true,
          total: tableData.totalElements,
          pageSize: pageSize,
          current: pageNum,
          onChange: paginationChange
        } }
      ></Table>
    </SelectExam>

    <ExamineeInfoModal
      ref={ examineeInfoModalRef }
      examineeItem={ currentEditItem }
      activeTabs={ ['examineeInfo', 'applyFormTable'] }
      examineeCheckable={ true }
      uploadList={ getTableData }
      pageType={ 'check' }
    ></ExamineeInfoModal>
  </>)
}

export default ExamCheckManagement
